import React, { useState } from 'react';
import { Shield, AlertTriangle, CheckCircle, Mail } from 'lucide-react';
import { toast } from 'react-hot-toast';

const DoNotSell = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    requestType: 'do-not-sell'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate form submission
      await new Promise(resolve => setTimeout(resolve, 1000));
      toast.success('Your request has been submitted successfully');
      setFormData({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        requestType: 'do-not-sell'
      });
    } catch (error) {
      toast.error('Failed to submit request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  return (
    <div className="flex-grow bg-gray-50 py-16">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <div className="max-w-3xl mx-auto">
            <h1 className="text-4xl font-bold text-center mb-8">Do Not Sell My Information</h1>
            
            <div className="bg-blue-50 border-l-4 border-blue-500 p-6 mb-8">
              <div className="flex">
                <Shield className="h-6 w-6 text-blue-500 mr-3" />
                <div>
                  <h2 className="text-lg font-semibold text-blue-700">Your Privacy Rights</h2>
                  <p className="text-blue-600">
                    Under the California Consumer Privacy Act (CCPA) and other applicable privacy laws, you have the right to opt-out of the sale of your personal information.
                  </p>
                </div>
              </div>
            </div>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">What This Means</h2>
              <div className="bg-gray-50 p-6 rounded-lg">
                <p className="text-gray-700 mb-4">
                  When you submit a "Do Not Sell My Information" request, United eLoans will:
                </p>
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>Stop selling your personal information to third parties</li>
                  <li>Remove your information from our marketing lists</li>
                  <li>Ensure your data is not shared with new partners for marketing purposes</li>
                  <li>Process your request within 15 business days</li>
                </ul>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">Important Information</h2>
              <div className="bg-orange-50 p-6 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="h-6 w-6 text-orange-500 mr-3 mt-1" />
                  <div>
                    <p className="text-gray-700 mb-4">
                      Please note that submitting this request may affect our ability to provide you with loan matching services, as sharing your information with lenders is essential to our service. However, we will still honor your privacy preferences.
                    </p>
                    <p className="text-gray-700">
                      This request applies only to the sale of personal information and does not affect other business purposes such as fraud prevention, legal compliance, or service provision.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">Submit Your Request</h2>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      First Name *
                    </label>
                    <input
                      type="text"
                      name="firstName"
                      value={formData.firstName}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-brand-red transition-colors"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Last Name *
                    </label>
                    <input
                      type="text"
                      name="lastName"
                      value={formData.lastName}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-brand-red transition-colors"
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-brand-red transition-colors"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    name="phoneNumber"
                    value={formData.phoneNumber}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-brand-red transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Request Type *
                  </label>
                  <select
                    name="requestType"
                    value={formData.requestType}
                    onChange={handleChange}
                    className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-brand-red focus:border-brand-red transition-colors"
                    required
                  >
                    <option value="do-not-sell">Do Not Sell My Information</option>
                    <option value="delete-data">Delete My Personal Information</option>
                    <option value="access-data">Access My Personal Information</option>
                  </select>
                </div>

                <div className="text-center">
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full sm:w-auto px-8 py-3 text-base font-medium rounded-lg text-white bg-brand-red hover:bg-brand-red-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-red transition-all transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center justify-center">
                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Processing...
                      </span>
                    ) : (
                      'Submit Request'
                    )}
                  </button>
                </div>
              </form>
            </section>

            <section className="mb-8">
              <h2 className="text-2xl font-bold mb-4">Alternative Contact Methods</h2>
              <div className="bg-gray-50 p-6 rounded-lg">
                <div className="flex items-start">
                  <Mail className="h-6 w-6 text-gray-500 mr-3 mt-1" />
                  <div>
                    <p className="text-gray-700 mb-2">
                      You can also submit your request by emailing us directly at:
                    </p>
                    <p className="text-brand-red font-semibold">info@unitedeloan.com</p>
                    <p className="text-sm text-gray-600 mt-2">
                      Please include your full name, email address, and phone number in your request.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            <div className="bg-green-50 p-6 rounded-lg">
              <div className="flex items-start">
                <CheckCircle className="h-6 w-6 text-green-500 mr-3 mt-1" />
                <div>
                  <h3 className="text-lg font-semibold text-green-700 mb-2">Your Rights Are Protected</h3>
                  <p className="text-green-600">
                    United eLoans is committed to protecting your privacy rights. We will not discriminate against you for exercising your privacy rights under applicable laws.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DoNotSell;